/**
 * The ledger: who owes whom.
 *
 * With three people a pairwise matrix is clearer than debt simplification, so
 * pairwise is the default and "simplified" is offered as a VIEW on top of it
 * (docs/01 §7.2) — the same balances, fewer transfers, never a different set of
 * net positions.
 *
 * **Guests never appear here.** They can sit in a food-order split and affect
 * the arithmetic, and then they disappear: their share is settled on the day.
 * The exclusion is enforced twice over — by `settledImmediately` on the split
 * itself, and by the `isGuest` predicate handed to `computeLedger`, which drops
 * a guest from every side of every row even if bad data puts one in a payer or
 * settlement column.
 */

import { useMemo, useState } from 'react'
import { type MemberId, type PairBalance, type Piastres, type TrustedClock, piastres } from '@office/domain'
import { computeLedger, netBalance, simplifyLedger } from '@office/domain/money'
import { type MoneySnapshot, guestPredicate, nameOf } from './data.js'
import { formatInstant } from './format.js'
import { Empty, Money, RadioGroup, Section } from './ui.js'
import './money.css'

export type LedgerView = 'pairwise' | 'simplified'

export interface LedgerScreenProps {
  snapshot: MoneySnapshot
  /** Time comes from the trusted clock, never the device (docs/02 §4). */
  clock: TrustedClock
  /** Whose phone this is. Their own position is shown first. */
  actorId: MemberId
  /** Offered on every row the actor is the debtor of. */
  onSettleUp?: (to: MemberId, suggested: Piastres) => void
}

export function LedgerScreen({ snapshot, clock, actorId, onSettleUp }: LedgerScreenProps) {
  const [view, setView] = useState<LedgerView>('pairwise')
  const now = clock.now()

  const ledger = useMemo(
    () => computeLedger(snapshot.expenses, snapshot.settlements, { isGuest: guestPredicate(snapshot) }),
    [snapshot],
  )
  const rows: PairBalance[] = useMemo(
    () => (view === 'simplified' ? simplifyLedger(ledger) : ledger.pairs),
    [ledger, view],
  )

  const mine = netBalance(ledger, actorId)

  return (
    <div className="m-screen" data-testid="ledger-screen">
      <h2 className="m-title">Balances</h2>
      <p className="m-asof">as of {formatInstant(now)}</p>

      <p className="m-hero" data-testid="ledger-net">
        {mine === 0 ? (
          <span className="m-hero-figure">You are all square</span>
        ) : (
          <>
            <span className="m-hero-label">{mine > 0 ? 'You are owed' : 'You owe'}</span>
            <span className="m-hero-figure">
              <Money value={piastres(Math.abs(mine))} tone={mine > 0 ? 'ok' : 'primary'} />
            </span>
          </>
        )}
      </p>

      <RadioGroup
        legend="View"
        name="ledger-view"
        value={view}
        onChange={setView}
        options={[
          { value: 'pairwise', label: 'Pairwise' },
          { value: 'simplified', label: 'Simplified' },
        ]}
      />

      <Section
        title={view === 'simplified' ? 'Fewest transfers' : 'Between each pair'}
        count={rows.length}
      >
        {rows.length === 0 ? (
          <Empty hint="Every expense so far is squared off. New ones land here the moment somebody adds them.">
            Nobody owes anybody anything.
          </Empty>
        ) : (
          <ul className="m-list" aria-label={view === 'simplified' ? 'Simplified transfers' : 'Pairwise balances'}>
            {rows.map(row => {
              const fromName = row.from === actorId ? 'You' : nameOf(snapshot, row.from)
              const toName = row.to === actorId ? 'you' : nameOf(snapshot, row.to)
              return (
                <li
                  key={`${row.from}-${row.to}`}
                  className={`m-row${row.from === actorId ? ' m-row-mine' : ''}`}
                >
                  <span className="m-row-main">
                    {fromName} {row.from === actorId ? 'owe' : 'owes'} {toName}
                  </span>
                  <Money value={row.amount} />
                  {row.from === actorId && onSettleUp && (
                    <button
                      type="button"
                      className="btn m-row-action"
                      onClick={() => onSettleUp(row.to, row.amount)}
                    >
                      Settle up
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </Section>

      <p className="m-note">
        Guests split the bill on the day and never carry a balance, so they are
        not listed here.
      </p>
    </div>
  )
}
